import type { DatasetViewColumn } from '../../../../api/types';
import { resolveEntityDisplayName } from '../../../../utils/dataModelDisplayNames.ts';

/** Fallback only: known aliases and titles must not be humanized. */
export function humanizeEntity(raw: string): string {
  return raw
    .replace(/_view$/, '')
    .replace(/_dim$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function entityDisplayName(name: string, labels: ReadonlyMap<string, string>): string {
  return resolveEntityDisplayName(name, labels) ?? humanizeEntity(name);
}

export interface EntityFields {
  entity: string;
  label: string;
  fields: DatasetViewColumn[];
}

const ENTITY_TYPE_PRIORITY: Record<string, number> = {
  grain_fact: 0,
  fact: 1,
  dimension: 2,
};

function dominantEntityType(columns: DatasetViewColumn[]): number {
  return columns.reduce((best, col) => Math.min(best, ENTITY_TYPE_PRIORITY[col.entity_type ?? ''] ?? 3), 3);
}

/** Group by stored identifiers, never by potentially duplicate display labels. */
export function groupByEntity(
  columns: DatasetViewColumn[],
  labels: ReadonlyMap<string, string>,
): EntityFields[] {
  const groups = new Map<string, DatasetViewColumn[]>();
  for (const col of columns) {
    const entity = col.source_entity || '__ungrouped__';
    const fields = groups.get(entity);
    if (fields) fields.push(col);
    else groups.set(entity, [col]);
  }
  return Array.from(groups, ([entity, fields]) => ({
    entity,
    label: entity === '__ungrouped__' ? 'Fields' : entityDisplayName(entity, labels),
    fields,
  })).sort((a, b) => dominantEntityType(a.fields) - dominantEntityType(b.fields));
}

export function filterEntityFields(
  columns: DatasetViewColumn[],
  search: string,
  labels: ReadonlyMap<string, string>,
): DatasetViewColumn[] {
  const query = search.trim().toLowerCase();
  if (!query) return columns;
  return columns.filter((col) =>
    col.name.toLowerCase().includes(query) ||
    (col.display_name?.toLowerCase().includes(query) ?? false) ||
    (col.source_entity ? (
      col.source_entity.toLowerCase().includes(query) ||
      entityDisplayName(col.source_entity, labels).toLowerCase().includes(query)
    ) : false),
  );
}